// Local (browser) persistence for birth charts — no accounts, no backend.
// Shaped to map cleanly onto a future server-side /api/charts endpoint.

const LAST_KEY = 'astrela_last_chart';
const SAVED_KEY = 'astrela_saved_charts';

function read(key) {
  try {
    const v = localStorage.getItem(key);
    return v ? JSON.parse(v) : null;
  } catch {
    return null; // private mode / storage disabled — degrade gracefully
  }
}

function write(key, val) {
  try {
    localStorage.setItem(key, JSON.stringify(val));
    return true;
  } catch {
    return false;
  }
}

// Stable identity for a chart, derived from its birth data — used to dedupe
// saves and to tell whether the chart on screen is already saved.
export function chartKey(chart) {
  const b = (chart && chart.birthData) || {};
  const parts = [b.date, b.time, b.latitude, b.longitude];
  // Distinguish the same birth data saved under different house systems, so a
  // re-save under a new system no longer silently overwrites the first.
  // Backward-compatible: Placidus (the historical default) keeps the original
  // 4-part key, so charts saved before this change still dedupe correctly.
  if (b.houseSystem && b.houseSystem !== 'placidus') parts.push(b.houseSystem);
  return parts.join('|');
}

export function defaultLabel(chart) {
  const d = chart && chart.birthData && chart.birthData.date;
  return d ? `Chart · ${d}` : 'My chart';
}

// --- last viewed chart (so a refresh never wipes work) ---
export function getLastChart() {
  const v = read(LAST_KEY);
  return v && v.chart ? v : null; // { chart, unknownTime }
}

export function setLastChart(chart, unknownTime) {
  if (!chart) return;
  write(LAST_KEY, { chart, unknownTime: !!unknownTime });
}

// --- saved-charts library ---
export function getSavedCharts() {
  const v = read(SAVED_KEY);
  return Array.isArray(v) ? v : [];
}

export function saveChart(chart, unknownTime, label) {
  if (!chart) return null;
  const id = chartKey(chart);
  const entry = {
    id,
    label: (label && label.trim()) || defaultLabel(chart),
    savedAt: new Date().toISOString(),
    unknownTime: !!unknownTime,
    chart,
  };
  // Replace any existing entry for the same chart, newest first.
  const next = [entry, ...getSavedCharts().filter((e) => e.id !== id)];
  write(SAVED_KEY, next);
  return next;
}

export function deleteSavedChart(id) {
  const next = getSavedCharts().filter((e) => e.id !== id);
  write(SAVED_KEY, next);
  return next;
}
